import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { ArrowRight } from 'lucide-react';
import Footer from '@/components/Footer';
import Navigation from '@/components/Navigation';
import FloatingActionButtons from '@/components/FloatingActionButtons';

const OmOss = () => {
  return (
    <>
      <Navigation />
      <Helmet>
        <title>Om Oss - RenGlans Städtjänster</title>
        <meta name="description" content="Lär känna RenGlans - ditt pålitliga städföretag med över 25 års erfarenhet av professionell städning för hem och företag." />
        <meta property="og:title" content="Om Oss - RenGlans Städtjänster" />
        <meta property="og:description" content="Lär känna RenGlans - ditt pålitliga städföretag med över 25 års erfarenhet av professionell städning." />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-[80vh] pt-32 pb-20 flex items-center justify-center overflow-hidden" style={{ minHeight: 'calc(80vh - 120px)' }}>
        {/* Background Image with Blur */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: 'url(/house-cleaners.webp)',
            filter: 'blur(4px)',
            transform: 'scale(1.02)',
            zIndex: 0,
          }}
        >
          {/* Dark Overlay */}
          <div className="absolute inset-0 bg-[hsl(var(--hero-overlay))]/40" />
        </div>
        
        <div className="container mx-auto px-4 text-center relative z-10 text-white">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-5xl md:text-7xl lg:text-8xl font-extrabold mb-6"
          >
            Om Oss
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-xl md:text-2xl max-w-3xl mx-auto mb-8"
          >
            Med passion för renhet och service sedan 1995
          </motion.p>
        </div>
        <div className="absolute bottom-0 left-0 w-full overflow-hidden translate-y-px">
          <svg 
            xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none"
            className="w-full h-12 md:h-16"
            
          >
            <path d="M0,60 C200,120 400,0 600,60 C800,120 1000,0 1200,60 V120 H0 V60 Z" fill="#FEF2F2" />
          </svg>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 bg-red-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="relative w-full h-[28rem] md:h-[32rem] rounded-lg overflow-hidden flex items-start justify-center -mt-16"
              >
                <div className="relative w-full h-full rounded-lg overflow-hidden">
                  <img
                    src="/renglans2.webp"
                    alt="Vårt team på RenGlans"
                    className="w-full h-full object-contain object-center scale-110"
                  />
                </div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Vår Resa Hittills</h2>
                <p className="text-gray-600 mb-6 text-lg">
                  RenGlans grundades 1995 med en enkel vision - att leverera exceptionell städservice med fokus på kvalitet och kundnöjdhet. Från en liten verksamhet i Stockholm har vi vuxit till ett av regionens mest pålitliga städföretag.
                </p>
                <p className="text-gray-600 mb-8 text-lg">
                  Idag är vi ett team på över 50 engagerade medarbetare som varje dag arbetar för att göra våra kunders vardag lättare. Vår passion för renhet och service har gjort oss till en föredragen partner för både privatpersoner och företag.
                </p>
                <Button 
                  className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 text-lg"
                  onClick={() => window.location.href = '/'}
                >
                  Få en gratis offert
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Möt Teamet</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Här är några av de engagerade medarbetare som gör RenGlans till en framgångsrik städfirma.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[
              {
                name: 'Anna Karlsson',
                role: 'VD & Grundare',
                bio: 'Med över 30 års erfarenhet av branschen har Anna en passion för renhet och service.',
                image: '/team-anna.jpg'
              },
              {
                name: 'Erik Lundqvist',
                role: 'Operativ Chef',
                bio: 'Ansvarar för att alla städuppdrag utförs enligt våra höga kvalitetskrav.',
                image: '/team-erik.jpg'
              },
              {
                name: 'Maria Berg',
                role: 'Kundservice',
                bio: 'Maria är den glada rösten som hjälper dig med alla dina frågor och bokningar.',
                image: '/team-maria.jpg'
              }
            ].map((member, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
                className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300"
              >
                <div className="relative h-80 bg-gray-100">
                  <div className="absolute inset-0 flex items-center justify-center text-gray-400">
                    <span className="text-6xl">👤</span>
                  </div>
                </div>
                <div className="p-6 text-center">
                  <h3 className="text-xl font-bold text-gray-900">{member.name}</h3>
                  <p className="text-red-600 font-medium mb-4">{member.role}</p>
                  <p className="text-gray-600">{member.bio}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Red Wave Divider */}
      <div className="relative -mb-px">
        <div className="absolute bottom-0 left-0 w-full overflow-hidden translate-y-px" style={{ height: '80px', zIndex: 10 }}>
          <svg 
            viewBox="0 0 1200 80" 
            preserveAspectRatio="none"
            className="w-full h-full block"
            shapeRendering="geometricPrecision"
          >
            <path d="M0,0C0,0,100,60,300,40S600,20,900,40s300,20,300,20v20H0V0z" className="fill-red-500" />
          </svg>
        </div>
      </div>

      {/* CTA Section */}
      <section 
        className="relative isolate py-20 bg-red-500 text-white overflow-hidden"
        style={{ transform: 'translateZ(0)', backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden' }}
      >
        <div className="pointer-events-none absolute inset-0 bg-red-500" aria-hidden />
        <div className="pointer-events-none absolute inset-x-0 top-0 h-4 bg-red-500" aria-hidden />
        <div className="container mx-auto px-4 text-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Redo att uppleva RenGlans skillnad?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Boka din kostnadsfria offert idag och låt oss ta hand om dina städbehov.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              className="bg-white text-red-600 hover:bg-gray-100 text-lg px-8 py-6 uppercase font-normal tracking-wide"
              onClick={() => window.location.href = '/'}
            >
              Få Gratis Offert
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button 
              variant="outline"
              size="lg"
              className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-red-600 text-lg px-8 py-6 uppercase font-normal tracking-wide"
              onClick={() => window.location.href = '/'}
            >
              Få Offert
            </Button>
          </div>
        </div>
      </section>
      
      <Footer />
      <FloatingActionButtons />
    </>
  );
};

export default OmOss;
